import { inflate } from 'pako';

import { REACT_APP_BE_API_URL } from '#shared';

import { fetchWithAuthHeaders } from '../fetchWithAuthHeaders';

import type { BaseGrib2Message } from '#entities';
import type {
  ForecastDataSetFile,
  WindsForecastGridSearchParams,
  WindsForecastSearchParams,
} from './queries';

// SpecRef: ARCH-002
// Reference: docs/decisions/ARCH-002-webgl-heatmap-migration.md#fe-png-fetch--primary-step-6
export interface BinaryWindResponse {
  data: Uint8Array;
  width: number;
  height: number;
  maxWindSpeed: number;
}

// SpecRef: ARCH-002 (primary transport)
// Reference: docs/decisions/ARCH-002-webgl-heatmap-migration.md#pngwindresponse-type
export interface PngWindResponse {
  blob: Blob;
  width: number;
  height: number;
  maxWindSpeed: number;
}

const paths = {
  airSegmets: 'api/aviation-weather/airsigmets.cache.csv.gz',
  iSegmet: 'api/aviation-weather/isigmet',
  metars: 'api/aviation-weather/metars.cache.csv.gz',
  pireps: 'api/aviation-weather/aircraftreports.cache.csv.gz',
  tafs: 'api/aviation-weather/tafs',
  windsForecast: 'api/aviation-weather/winds-forecast',
  windsForecastGrid: 'api/aviation-weather/winds-forecast-grid',
} as const;

const decompressGz = async (res: Response) => {
  const arrayBuffer = await res.arrayBuffer();
  const buffer = inflate(arrayBuffer);
  return new TextDecoder().decode(buffer);
};

const fetchWeather = async (path: string) => {
  const url = `${REACT_APP_BE_API_URL}${path}`;
  const res = await fetchWithAuthHeaders(url, false);
  if (!res.ok) {
    throw new Error(`Failed fetch: ${res.status}`);
  }
  return path.includes('.gz') ? decompressGz(res) : res.json();
};

export const weatherApi = {
  getAirSegment: async () => fetchWeather(paths.airSegmets),
  getISegmet: async () => fetchWeather(paths.iSegmet),
  getMetars: async () => fetchWeather(paths.metars),
  getPireps: async () => fetchWeather(paths.pireps),
  getTafs: async () => fetchWeather(paths.tafs),

  getWindsForecast: async (
    params: WindsForecastSearchParams<keyof ForecastDataSetFile>,
  ): Promise<BaseGrib2Message[]> => {
    const url = `${paths.windsForecast}?${new URLSearchParams(
      Object.entries(params).map(([k, v]) => [k, String(v)]),
    )}`;
    return fetchWeather(url);
  },
  getWindsForecastGrid: async (
    params: WindsForecastGridSearchParams,
  ): Promise<{ width: number; height: number; data: number[] }> => {
    const url = `${paths.windsForecastGrid}?${new URLSearchParams(
      Object.entries(params).map(([k, v]) => [k, String(v)]),
    )}`;
    return fetchWeather(url);
  },
  getWindsForecastGridBinary: async (
    params: WindsForecastGridSearchParams,
  ): Promise<BinaryWindResponse> => {
    const url = `${paths.windsForecastGrid}?${new URLSearchParams(
      Object.entries({ ...params, format: 'binary' })
        .filter(([k]) => k !== 'dateTime')
        .map(([k, v]) => [k, String(v)]),
    )}`;
    const fullUrl = `${REACT_APP_BE_API_URL}${url}`;
    const res = await fetchWithAuthHeaders(fullUrl, false);
    if (!res.ok) {
      throw new Error(`Binary fetch failed: ${res.status}`);
    }

    const width = Number(res.headers.get('X-Wind-Grid-Width') ?? 2048);
    const height = Number(res.headers.get('X-Wind-Grid-Height') ?? 1024);
    const maxWindSpeed = Number(res.headers.get('X-Wind-Max-Speed') ?? 30);
    const buffer = await res.arrayBuffer();
    return { data: new Uint8Array(buffer), width, height, maxWindSpeed };
  },
  // SpecRef: ARCH-002 (primary PNG transport)
  // Reference: docs/decisions/ARCH-002-webgl-heatmap-migration.md#new-api-method-getwindsForecastgridpng
  getWindsForecastGridPng: async (
    params: WindsForecastGridSearchParams,
  ): Promise<PngWindResponse> => {
    const url = `${paths.windsForecastGrid}?${new URLSearchParams(
      Object.entries({ ...params, format: 'png' })
        .filter(([k]) => k !== 'dateTime')
        .map(([k, v]) => [k, String(v)]),
    )}`;
    const fullUrl = `${REACT_APP_BE_API_URL}${url}`;
    const res = await fetchWithAuthHeaders(fullUrl, false);
    if (!res.ok) {
      throw new Error(`PNG fetch failed: ${res.status}`);
    }

    const width = Number(res.headers.get('X-Wind-Grid-Width') ?? 2048);
    const height = Number(res.headers.get('X-Wind-Grid-Height') ?? 1024);
    const maxWindSpeed = Number(res.headers.get('X-Wind-Max-Speed') ?? 30);
    const blob = await res.blob();
    return { blob, width, height, maxWindSpeed };
  },
};
